import React, { useState, useEffect } from 'react'

interface ModelData {
  id?: number
  name?: string
  timeframe?: {
    from: string
    to: string
  }
  dependent_driver_id?: number
  parameters?: Record<string, any>
  independent_drivers?: Array<{
    driver_id: number
    [key: string]: any
  }>
  [key: string]: any
}

interface DiffTextProps {
  oldValue: any
  newValue: any
}

interface ArrayItem {
  [key: string]: any
}

interface ArrayComparisonProps {
  oldArray?: ArrayItem[]
  newArray?: ArrayItem[]
  idKey: string
  nameKey?: string
  driverNames?: Record<number | string, string>
}

interface ObjectComparisonProps {
  oldObj?: Record<string, any>
  newObj?: Record<string, any>
}

interface ModelComparerProps {
  newModel: ModelData
  oldModel: ModelData
  projectId?: number | string
  solutionId?: number | string
}

interface ModelViewerProps {
  model: ModelData
  projectId?: number | string
  solutionId?: number | string
}

// Component to display difference between two values
const DiffText: React.FC<DiffTextProps> = ({ oldValue, newValue }) => {
  if (oldValue === newValue) return <span>{newValue}</span>

  return (
    <>
      <span style={{ textDecoration: 'line-through', color: '#888888' }}>
        {oldValue}
      </span>{' '}
      <span style={{ color: '#00AA00' }}>{newValue}</span>
    </>
  )
}

// Component for comparing arrays of objects
const ArrayComparison: React.FC<ArrayComparisonProps> = ({
  oldArray = [],
  newArray = [],
  idKey,
  nameKey = 'name',
  driverNames = {},
}) => {
  if (!Array.isArray(oldArray)) oldArray = []
  if (!Array.isArray(newArray)) newArray = []

  const oldItems = new Map<any, ArrayItem>()

  oldArray.forEach((item) => {
    if (item && item[idKey]) {
      oldItems.set(item[idKey], item)
    }
  })

  const newItems = new Map<any, ArrayItem>()

  newArray.forEach((item) => {
    if (item && item[idKey]) {
      newItems.set(item[idKey], item)
    }
  })

  // Helper to get display name for a driver
  const getDisplayName = (item: ArrayItem): string => {
    const id = item[idKey]

    // Try to get name from the driverNames lookup first
    if (driverNames[id]) return driverNames[id]

    // Fall back to the name property or ID
    return item[nameKey] || id
  }

  // Removed items (in old but not in new)
  const removedItems = oldArray.filter(
    (item) => item && item[idKey] && !newItems.has(item[idKey]),
  )

  // New items (in new but not in old)
  const addedItems = newArray.filter(
    (item) => item && item[idKey] && !oldItems.has(item[idKey]),
  )

  // Unchanged items (in both)
  const unchangedItems = newArray.filter(
    (item) => item && item[idKey] && oldItems.has(item[idKey]),
  )

  return (
    <ul style={{ margin: 0, paddingLeft: '20px' }}>
      {removedItems.map((item, index) => (
        <li key={`removed-${index}`}>
          <span style={{ textDecoration: 'line-through', color: '#888888' }}>
            {getDisplayName(item)}
          </span>
        </li>
      ))}

      {addedItems.map((item, index) => (
        <li key={`added-${index}`}>
          <span style={{ color: '#00AA00' }}>
            {'+ ' + getDisplayName(item)}
          </span>
        </li>
      ))}

      {unchangedItems.map((item, index) => (
        <li key={`unchanged-${index}`}>{getDisplayName(item)}</li>
      ))}
    </ul>
  )
}

// Component for comparing objects
const ObjectComparison: React.FC<ObjectComparisonProps> = ({
  oldObj = {},
  newObj = {},
}) => {
  if (!oldObj) oldObj = {}
  if (!newObj) newObj = {}

  const allKeys = [...new Set([...Object.keys(oldObj), ...Object.keys(newObj)])]

  return (
    <ul style={{ margin: 0, paddingLeft: '20px' }}>
      {allKeys.map((key, index) => {
        const oldVal = oldObj[key]
        const newVal = newObj[key]

        return (
          <li key={`${key}-${index}`}>
            <strong>{key}:</strong>{' '}
            <DiffText oldValue={oldVal} newValue={newVal} />
          </li>
        )
      })}
    </ul>
  )
}

export const ModelComparer: React.FC<ModelComparerProps> = ({
  newModel,
  oldModel,
  projectId,
  solutionId,
}) => {
  const [driverNames, setDriverNames] = useState<Record<number, string>>({})
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Fetch driver names when the component mounts
    const fetchDriverNames = async () => {
      if (!projectId || !solutionId) {
        setLoading(false)
        return
      }

      try {
        // Full-list id→name lookup for comparison (not a table); bounded
        // server-side at 9999 (BaseCitrusPagination max_page_size).
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/v2/projects/${projectId}/solutions/${solutionId}/drivers`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          },
        )

        if (!response.ok) {
          throw new Error('Failed to fetch drivers')
        }

        const data = await response.json()

        // Create a lookup map from driver ID to driver name
        const nameMap: Record<number, string> = {}

        data.data.forEach((item: any) => {
          nameMap[item.driver.id] = item.driver.name
        })

        setDriverNames(nameMap)
      } catch (error) {
        console.error('Error fetching driver names:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchDriverNames()
  }, [projectId, solutionId])

  if (!oldModel) return <div>No previous version to compare with</div>
  if (!newModel) return <div>No new model data to compare</div>
  if (loading) return <div>Loading comparison...</div>

  // Check if there are any differences
  const modelHasChanges = hasChanges(newModel, oldModel)

  return (
    <div style={{ padding: '10px', color: '#333', fontSize: '12px' }}>
      <h4>Model Changes</h4>

      {!modelHasChanges ? (
        <div
          style={{
            backgroundColor: '#eaffef',
            padding: '8px',
            borderRadius: '4px',
            marginBottom: '12px',
            color: '#00AA00',
            fontWeight: 'bold',
          }}
        >
          No changes!
        </div>
      ) : null}

      <div
        style={{
          marginTop: '8px',
          marginBottom: '8px',
          fontWeight: modelHasChanges ? 'normal' : 'bold',
        }}
      >
        {modelHasChanges ? null : 'Current data:'}
      </div>

      {/* Compare model name */}
      <p>
        <strong>Name:</strong>{' '}
        <DiffText oldValue={oldModel.name} newValue={newModel.name} />
      </p>

      {/* Compare timeframe */}
      {oldModel.timeframe && newModel.timeframe && (
        <p>
          <strong>Timeframe:</strong>{' '}
          <DiffText
            oldValue={`${oldModel.timeframe.from} to ${oldModel.timeframe.to}`}
            newValue={`${newModel.timeframe.from} to ${newModel.timeframe.to}`}
          />
        </p>
      )}

      {/* Compare dependent driver - now using strikethrough instead of "Changed from" text */}
      {oldModel.dependent_driver_id !== newModel.dependent_driver_id && (
        <p>
          <strong>Outcome Variable:</strong>{' '}
          <DiffText
            oldValue={
              driverNames[oldModel.dependent_driver_id || 0] ||
              oldModel.dependent_driver_id
            }
            newValue={
              driverNames[newModel.dependent_driver_id || 0] ||
              newModel.dependent_driver_id
            }
          />
        </p>
      )}

      {/* Compare parameters */}
      <p>
        <strong>Parameters:</strong>
        <ObjectComparison
          oldObj={oldModel.parameters}
          newObj={newModel.parameters}
        />
      </p>

      {/* Compare independent drivers */}
      <p>
        <strong>Explanatory Variables:</strong>
        <ArrayComparison
          oldArray={oldModel.independent_drivers}
          newArray={newModel.independent_drivers}
          idKey="driver_id"
          driverNames={driverNames}
        />
      </p>
    </div>
  )
}

// New component to display a single model (no comparison)
export const ModelViewer: React.FC<ModelViewerProps> = ({
  model,
  projectId,
  solutionId,
}) => {
  const [driverNames, setDriverNames] = useState<Record<number, string>>({})
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Fetch driver names when the component mounts
    const fetchDriverNames = async () => {
      if (!projectId || !solutionId) {
        setLoading(false)
        return
      }

      try {
        // Full-list id→name lookup for comparison (not a table); bounded
        // server-side at 9999 (BaseCitrusPagination max_page_size).
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/v2/projects/${projectId}/solutions/${solutionId}/drivers`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          },
        )

        if (!response.ok) {
          throw new Error('Failed to fetch drivers')
        }

        const data = await response.json()

        // Create a lookup map from driver ID to driver name
        const nameMap: Record<number, string> = {}

        data.data.forEach((item: any) => {
          nameMap[item.driver.id] = item.driver.name
        })

        setDriverNames(nameMap)
      } catch (error) {
        console.error('Error fetching driver names:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchDriverNames()
  }, [projectId, solutionId])

  if (!model) return <div>No model data to display</div>
  if (loading) return <div>Loading model data...</div>

  return (
    <div style={{ padding: '10px', color: '#333', fontSize: '12px' }}>
      <h4>Model Details</h4>

      {/* Display model name */}
      <p>
        <strong>Name:</strong> {model.name}
      </p>

      {/* Display timeframe */}
      {model.timeframe && (
        <p>
          <strong>Timeframe:</strong>{' '}
          {`${model.timeframe.from} to ${model.timeframe.to}`}
        </p>
      )}

      {/* Display dependent driver */}
      <p>
        <strong>Outcome Variable:</strong>{' '}
        {driverNames[model.dependent_driver_id || 0] ||
          model.dependent_driver_id}
      </p>

      {/* Display parameters */}
      <p>
        <strong>Parameters:</strong>
        <ul style={{ margin: 0, paddingLeft: '20px' }}>
          {Object.entries(model.parameters || {}).map(([key, value], index) => (
            <li key={index}>
              {key}: {String(value)}
            </li>
          ))}
        </ul>
      </p>

      {/* Display independent drivers */}
      <p>
        <strong>Explanatory Variables:</strong>
        <ul style={{ margin: 0, paddingLeft: '20px' }}>
          {(model.independent_drivers || []).map((driver, index) => {
            const driverId = driver.driver_id
            const driverName = driverNames[driverId] || driverId

            return <li key={index}>{driverName}</li>
          })}
        </ul>
      </p>
    </div>
  )
}

const hasChanges = (newModel: ModelData, oldModel: ModelData): boolean => {
  if (!oldModel || !newModel) return true

  // Check name
  if (newModel.name !== oldModel.name) return true

  // Check timeframe
  if (newModel.timeframe && oldModel.timeframe) {
    if (
      newModel.timeframe.from !== oldModel.timeframe.from ||
      newModel.timeframe.to !== oldModel.timeframe.to
    ) {
      return true
    }
  }

  // Check dependent driver
  if (newModel.dependent_driver_id !== oldModel.dependent_driver_id) return true

  // Check parameters
  const newParams = newModel.parameters || {}
  const oldParams = oldModel.parameters || {}
  const allParamKeys = [
    ...new Set([...Object.keys(newParams), ...Object.keys(oldParams)]),
  ]

  for (const key of allParamKeys) {
    if (newParams[key] !== oldParams[key]) return true
  }

  // Check independent drivers
  const newDrivers = newModel.independent_drivers || []
  const oldDrivers = oldModel.independent_drivers || []

  if (newDrivers.length !== oldDrivers.length) return true

  // Convert driver arrays to sets of IDs for comparison
  const newDriverIds = new Set(newDrivers.map((d) => d.driver_id))
  const oldDriverIds = new Set(oldDrivers.map((d) => d.driver_id))

  // Check if any driver is in one set but not the other
  for (const id of newDriverIds) {
    if (!oldDriverIds.has(id)) return true
  }

  for (const id of oldDriverIds) {
    if (!newDriverIds.has(id)) return true
  }

  return false
}

// Export the hasChanges function so it can be used elsewhere
export { hasChanges }

// Wrapper function for backward compatibility and non-React contexts
export const Compare = (
  newModel: ModelData,
  oldModel: ModelData,
  projectId?: number | string,
  solutionId?: number | string,
): React.ReactElement => {
  // Returns JSX element that can be rendered
  return (
    <ModelComparer
      newModel={newModel}
      oldModel={oldModel}
      projectId={projectId}
      solutionId={solutionId}
    />
  )
}

export default {
  Compare,
  ModelComparer,
  ModelViewer,
  hasChanges,
}
